import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { authAPI } from '../../services/api';
import { User } from '../../types';
import toast from 'react-hot-toast';
import { updateProfile } from './userSlice';

interface AuthState {
user: User | null;
  role: string;
  token: string | null;
  isAuthenticated: boolean;
  loading: boolean;
  error: string | null;
}


// Async thunks
export const register = createAsyncThunk(
  'auth/register',
  async (userData: any, { rejectWithValue }) => {
    try {
      const response = await authAPI.register(userData);
      return response.data.data;
    } catch (error: any) {
        // console.log(error)
      return rejectWithValue(error.response?.data?.error || 'Registration failed');
    }
  }
);

export const login = createAsyncThunk(
  'auth/login',
  async (credentials: any, { rejectWithValue }) => {
    try {
      const response = await authAPI.login(credentials);
      return response.data.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Login failed');
    }
  }
);

export const googleLogin = createAsyncThunk(
  'auth/googleLogin',
  async (data: { id_token?: string; access_token?: string }, { rejectWithValue }) => {
    try {
      const response = await authAPI.googleLogin(data);
      return response.data;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.error || 'Google login failed');
    }
  }
);

// Fetch latest user profile to keep roles/tiers synchronized
export const fetchCurrentUser = createAsyncThunk(
  'auth/fetchCurrentUser',
  async (userId: string, { rejectWithValue }) => {
    try {
      // Assuming we have access to userAPI, we'll import it at the top if needed. Let's use it.
      const { userAPI } = await import('../../services/api');
      const response = await userAPI.getProfile(userId);
      return response.data.data.user;
    } catch (error: any) {
      console.error('Failed to sync current user session:', error);
      return rejectWithValue(error.response?.data?.error || 'Failed to fetch current user profile');
    }
  }
);

// Async thunk for logout including native sign-out handlers
export const logoutUser = createAsyncThunk(
  'auth/logoutUser',
  async (_, { dispatch }) => {
    try {
      // Lazy load to avoid web node build crashes
      const { Capacitor } = await import('@capacitor/core');
      if (Capacitor.isNativePlatform()) {
        const { GoogleAuth } = await import('@codetrix-studio/capacitor-google-auth');
        await GoogleAuth.signOut();
      }
    } catch (e) {
      console.error('Failed to sign out from native Google:', e);
    }
    dispatch(logout());
  }
);

// Add error handling for localStorage
const getStoredUser = () => {
    try {
      const user = localStorage.getItem('user');
      return user ? JSON.parse(user) : null;
    } catch {
      return null;
    }
  };
  
  const getStoredToken = () => {
    try {
      return localStorage.getItem('token') || null;
    } catch {
      return null;
    }
  };
  
  const initialState: AuthState = {
    user: getStoredUser(),
    token: getStoredToken(),
    isAuthenticated: !!getStoredToken(),
    loading: false,
    error: null,
    role: getStoredUser() != null ? getStoredUser().user_type : ""
  };
  

const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.token = null;
      state.isAuthenticated = false;
      localStorage.removeItem('token');
      localStorage.removeItem('user');
    },
    clearError: (state) => {
      state.error = null;
    },
    updateUser: (state, action: PayloadAction<Partial<User>>) => {
        if (state.user) {
          // Merge the updates instead of replacing
          state.user = { ...state.user, ...action.payload };
          
          // Update role if user_type changed
          if (action.payload.user_type) {
            state.role = action.payload.user_type;
          }
          
          // Also update localStorage
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      },
    //   updateLocalUser: (state, action: PayloadAction<User>) => {
    //     if(state.user) {
    //         state.user = { ...state.user, ...action.payload };
    //         localStorage.setItem('user', JSON.stringify(state.user));
    //     }
    //   },
      // Add this action to update user counts
    updateUserCounts: (state, action: PayloadAction<{
        followers_count: number;
        following_count: number;
        collaborations_count: number;
    }>) => {
        if (state.user) {
        state.user.followers_count = action.payload.followers_count;
        state.user.following_count = action.payload.following_count;
        state.user.collaborations_count = action.payload.collaborations_count;
        }
    },
    incrementPortfolioCount: (state) => { 
      // console.log('Incrementing portfolio count', state.user?.portfolio_items_count);
        if (state.user) {
          state.user.portfolio_items_count = (state.user.portfolio_items_count || 0) + 1;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
    },
    decrementPortfolioCount: (state) => {
        if (state.user && state.user.portfolio_items_count && state.user.portfolio_items_count > 0) {
          state.user.portfolio_items_count -= 1;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
    },
    incrementCollaborationCount: (state) => {
        if (state.user) {
          state.user.collaborations_count = (state.user.collaborations_count || 0) + 1;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
    },
  
  },
  extraReducers: (builder) => {
    builder
      // Register
      .addCase(register.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(register.fulfilled, (state, action) => {
        state.loading = false;
        // Check if registration requires verification (no token returned)
        if (!action.payload.token) {
          // Do not set user/token/isAuthenticated
          return;
        }
        
        state.user = action.payload.user;
        state.role = action.payload.user?.user_type || "";
        state.token = action.payload.token;
        state.isAuthenticated = true;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        // toast.success('Registration successful!');
      })
      .addCase(register.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        if (state.error !== 'ERR_NO_EARLY_ACCESS') {
          toast.error(action.payload as string);
        }
      })
      // Login
      .addCase(login.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.role = action.payload.user.user_type;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        // toast.success('Login successful!');
      })
      .addCase(login.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Google Login
      .addCase(googleLogin.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(googleLogin.fulfilled, (state, action) => {
        state.loading = false;
        state.user = action.payload.user;
        state.role = action.payload.user.user_type;
        state.token = action.payload.token;
        state.isAuthenticated = true;
        localStorage.setItem('token', action.payload.token);
        localStorage.setItem('user', JSON.stringify(action.payload.user));
        // toast.success('Login successful!');
      })
      .addCase(googleLogin.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
        if (state.error !== 'ERR_NO_EARLY_ACCESS') {
          toast.error(action.payload as string);
        }
      })
      // Sync Current User state
      .addCase(fetchCurrentUser.fulfilled, (state, action) => {
        if (state.user && action.payload) {
          state.user = { ...state.user, ...action.payload };
          state.role = action.payload.user_type || state.role;
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      })
      // Sync user state on profile update
      .addCase(updateProfile.fulfilled, (state, action: any) => {
        if (state.user && action.payload?.user) {
          const updatedUser = action.payload.user;
          const score = action.payload.completion_score ?? updatedUser.completion_score ?? state.user.completion_score;
          const isComplete = action.payload.is_complete ?? updatedUser.is_profile_complete ?? state.user.is_profile_complete;
          state.user = {
            ...state.user,
            ...updatedUser,
            completion_score: score,
            is_profile_complete: isComplete,
          };
          localStorage.setItem('user', JSON.stringify(state.user));
        }
      });
  },
});



export const { logout, clearError, updateUser, updateUserCounts, incrementPortfolioCount, decrementPortfolioCount, incrementCollaborationCount } = authSlice.actions;
export default authSlice.reducer;
